import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function useCountUp(target, duration = 1800, start) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);
  return value;
}

function Stat({ value, suffix, label, testId, start }) {
  const v = useCountUp(value, 1800, start);
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="relative glass rounded-2xl p-8 md:p-10 overflow-hidden group hover:-translate-y-1 hover:border-white/25 transition-all duration-500"
    >
      <div className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 rounded-full bg-indigo-500/20 blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative">
        <div className="font-display text-6xl md:text-7xl font-light tracking-tighter text-gradient leading-none">
          {v}
          <span className="text-indigo-300">{suffix}</span>
        </div>
        <div className="mt-5 text-sm md:text-base text-slate-300 max-w-[16ch] leading-snug">
          {label}
        </div>
      </div>
    </motion.div>
  );
}

export default function ProofOfImpact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section
      data-testid="impact-section"
      id="impact"
      ref={ref}
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12 md:mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-400">
            Proof of Impact
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tighter text-white">
            Numbers that <span className="text-gradient">compound.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <Stat
            testId="impact-stat-1"
            value={100}
            suffix="+"
            label="AI Workflows Deployed across regulated industries"
            start={inView}
          />
          <Stat
            testId="impact-stat-2"
            value={500}
            suffix="+"
            label="Specialized Experts Placed in mission-critical roles"
            start={inView}
          />
          <Stat
            testId="impact-stat-3"
            value={98}
            suffix="%"
            label="Client Satisfaction across renewals and referrals"
            start={inView}
          />
        </div>
      </div>
    </section>
  );
}
